import type { FC, ReactElement } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import type { IconComponentProps } from '@motorway/mw-highway-code';
import { Button } from '@motorway/mw-highway-code';

import { applyCypressData } from 'Utilities/helpers';

import { resolveQueryParamsWithPage } from '../Pagination.helpers';
import type { PaginationDirections } from '../Pagination.types';

type PaginationButtonProps = {
	cypressId: string;
	direction: PaginationDirections;
	icon: (props: IconComponentProps) => ReactElement;
	isDisabled: boolean;
	onClick?: (isButtonDisabled: boolean, pageNumber: number, direction: PaginationDirections) => void;
	page: number;
};

export const PaginationButton: FC<PaginationButtonProps> = ({
	cypressId,
	direction,
	icon: Icon,
	isDisabled,
	onClick,
	page,
}) => {
	const { pathname, query } = useRouter();

	const nextPage = direction === 'back' ? page - 1 : page + 1;

	return (
		<Link
			legacyBehavior
			passHref
			shallow
			href={{ pathname, query: resolveQueryParamsWithPage(nextPage, query) }}
			scroll={false}
		>
			<Button
				{...applyCypressData(`${cypressId}-${direction}`)}
				aria-label={`Go to ${direction === 'back' ? 'previous' : 'next'} page`}
				as="a"
				data-testid={`pagination-${direction}`}
				disabled={isDisabled}
				icon={Icon}
				label={`Paginate ${direction}`}
				onClick={() => onClick?.(isDisabled, nextPage, direction)}
				showLabel={false}
				variant="tertiary"
			/>
		</Link>
	);
};
